import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { FirebaseService } from '../../_services/firebase.service';
import { AuthService } from '../../_services/auth.service';
import { FbUser, JsUser } from '../../_interfaces/user.interface';
import { convertTimeStampsFb_Js } from '../../_utils/utils';
import { VersionServiceService } from '../../_services/version.service';
import { EmailService } from '../../_services/email.service';
import { AlertService } from '../../_services/alert.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-panel',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    MatToolbar,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatDrawer,
    MatMenu,
    MatDrawerContainer,
    MatDrawerContent,
    MatMenuTrigger,
    MatMenuItem,
  ],
  templateUrl: './panel.component.html',
  styleUrl: './panel.component.scss',
})
export class PanelComponent implements OnInit {
  showSidenav = true;
  currentUser: any = null;
  unSubscribe = new Subject<void>();

  // Environment info
  isProduction = environment.production;
  envLabel = environment.production ? 'PROD' : 'DEV';

  // Track if we're in email editor (to highlight Email Templates in sidebar)
  isInEmailEditor = false;

  /** Check if current user is admin */
  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  constructor(
    public fbs: FirebaseService,
    private authService: AuthService,
    public versionService: VersionServiceService,
    private emailService: EmailService,
    private alertService: AlertService,
    private router: Router
  ) {
    // Subscribe to router events to detect email-editor route
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.unSubscribe)
      )
      .subscribe((event: any) => {
        this.isInEmailEditor = event.url.startsWith('/email-editor');
      });
  }

  ngOnInit() {
    this.getUserList();
    this.emailService.init();
  }

  getUserList() {
    this.fbs
      .getFbUserData()
      .pipe(
        map((users: FbUser[]) => {
          return users.map((user) => convertTimeStampsFb_Js(user) as JsUser);
        }),
        map((users: JsUser[]) => {
          return users.map((user) => {
            return {
              label: user.name,
              value: user.id,
              roles: user.roles || [],
            };
          });
        }),
        takeUntil(this.unSubscribe)
      )
      .subscribe((users) => {
        const usersList = users
          // @ts-ignore
          .sort((a, b) => b.label - a.label);
        this.fbs.users = usersList;
        this.currentUser = this.fbs.getCurrentUser();
        usersList.forEach((user) => {
          this.fbs.uidUserMap[user.value] = user;
          if (user.value !== this.currentUser.value) {
            this.fbs.tagNameToUidMap[user.label] = user.value;
            this.fbs.uidToName[user.value] = user.label;
          }
        });
        this.fbs.currentUserSubject$.next(this.currentUser);
        this.fbs.currentUser = this.currentUser;
        this.versionService.check();
      });
  }

  toggleSidenav() {
    this.showSidenav = !this.showSidenav;
  }

  async logout() {
    const confirmed = await this.alertService.confirm('Logout', 'Are you sure you want to logout?');
    if (confirmed) {
      await this.authService.logoutUser();
    }
  }

  ngOnDestroy() {
    this.unSubscribe?.next();
    this.unSubscribe?.complete();
    this.versionService.stopSubscription();
    this.fbs.stopSubscription();
  }
}
